import type { Vector3 } from '@kitware/vtk.js/types';
import vtkPlaneManipulator from '@kitware/vtk.js/Widgets/Manipulators/PlaneManipulator';
import { vec3 } from 'gl-matrix';
import { ImageMetadata } from '../types/image';
import { LPSAxisDir } from '../types/lps';
import { getLPSAxisFromDir } from './lps';

export function updatePlaneManipulatorFor2DView(
  manipulator: vtkPlaneManipulator,
  viewDir: LPSAxisDir,
  slice: number,
  imageMetadata: ImageMetadata
) {
  const { lpsOrientation } = imageMetadata;
  const axis = lpsOrientation[getLPSAxisFromDir(viewDir)];

  const normal: vec3 = lpsOrientation[viewDir];
  const origin: vec3 = [0, 0, 0];
  origin[axis] = slice;

  vec3.transformMat4(origin, origin, imageMetadata.indexToWorld);

  manipulator.setUserNormal(normal as Vector3);
  manipulator.setUserOrigin(origin as Vector3);
}

export function createPlaneManipulatorFor2DView(
  viewDir: LPSAxisDir,
  slice: number,
  imageMetadata: ImageMetadata
) {
  const manipulator = vtkPlaneManipulator.newInstance();
  updatePlaneManipulatorFor2DView(manipulator, viewDir, slice, imageMetadata);
  return manipulator;
}

/** World-space origin and unit normal for a 2D volume slice (matches the widget plane). */
export function getVolume2DSlicePlaneInWorld(
  viewDir: LPSAxisDir,
  slice: number,
  imageMetadata: ImageMetadata
): { origin: Vector3; normal: Vector3 } {
  const { lpsOrientation } = imageMetadata;
  const axis = lpsOrientation[getLPSAxisFromDir(viewDir)];

  const normal = lpsOrientation[viewDir] as Vector3;
  const origin: vec3 = [0, 0, 0];
  origin[axis] = slice;
  vec3.transformMat4(origin, origin, imageMetadata.indexToWorld);

  return {
    origin: [origin[0], origin[1], origin[2]],
    normal: [normal[0], normal[1], normal[2]],
  };
}

/** Orthogonal projection of a world point onto a plane. */
export function projectPointOntoPlane(
  point: Vector3,
  planeOrigin: Vector3,
  planeNormal: Vector3
): Vector3 {
  const n = vec3.normalize(vec3.create(), planeNormal as vec3);
  const v = vec3.sub(vec3.create(), point as vec3, planeOrigin as vec3);
  const dist = vec3.dot(v, n);
  const out = vec3.create();
  vec3.scaleAndAdd(out, point as vec3, n, -dist);
  return [out[0], out[1], out[2]];
}
